import { Grid, InputLabel, Typography } from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'
import { Auto, CheckBox, NumberText, RadioGroup } from '../../core/controls'
import { EmResult, Worksheet } from '../../core/domain'
import { Driver, EmCalculatorConfiguration, EmCalculatorStore, NoteType } from '../../core/em-calculator-api'
import { FormApi, Option, formApi } from '../../core/form'
import { IcdCptApi } from '../../core/icd-cpt-api'
import { lightGray } from '../../core/theme'
import { WorksheetContext } from '../../core/visit-context'
import { AddButton } from '../common/navigation'
import { FieldValueProps } from '../common/presentation'
import { EmCategoryColumns } from './em-category'
import { emInterventionsApi } from './em-interventions-api'
import { EmInterventions } from './em-interventions'

type EmCalculatorProps = {
  api: EmCalculatorStore
  context?: WorksheetContext
  icdCpt?: IcdCptApi
}

type ServiceCategoriesProps = EmCalculatorProps & {
  conf: EmCalculatorConfiguration
  onDriverChange: (v: Driver) => void
  onCategoryChange: () => void
  onDriverCleared: () => void
}

function serviceCategories(props: ServiceCategoriesProps): FormApi {
  const { onDriverChange, onCategoryChange, onDriverCleared, api } = props
  const { supportedNotesAndDrivers } = props.conf || {}

  const ref = api((s) => s.request)
  const update = () => api.getState().setRequest(ref)

  function setNoteType(t: NoteType) {
    ref.emDriver = null
    ref.noppCategory = null

    if (t && t.drivers) {
      setDrivers(t.drivers)

      if (t.drivers.length === 1) {
        const driver = t.drivers[0]
        ref.emDriver = driver.driverName

        setDriver(driver)
        setNoppList(driver.noppList || [])
        api.getState().updateMinMax(driver)
        onDriverChange(driver)
      } else {
        setDriver(null)
        setNoppList([])
        onDriverCleared()
      }
      ref.noteType = t.noteType
    }

    onCategoryChange()
  }
  const disabled = api((s) => s.readonly)

  const nts = supportedNotesAndDrivers
  const nt = nts && nts[0]
  const ds = nt && nt.drivers
  const d = ds && ds[0]

  const form = formApi({
    disabled,
    values: {
      noteType: nt,
      emDriver: d,
      noppCategory: null,
    },
    onChange: () => {
      update()
    },
    fields: {
      noteType: {
        label: 'E/M Service Category',
        list: nts || [],
        listLabel: (v) => v.noteDescription,
        onChange: setNoteType,
      },
      emDriver: {
        label: 'E/M Sub Category',
        list: ds || [],
        listLabel: (v) => v.driverDescription,
        onChange: (t: Driver) => {
          api.getState().updateMinMax(t)

          ref.emDriver = t?.driverName
          ref.noppCategory = null
          if (t) {
            setNoppList(t.noppList || [])
            setNoppCategory('')
          } else {
            setNoppList([])
          }

          onDriverChange({
            ...ref,
            ...{
              labelsOPT: t?.labelsOPT,
              labelsDATA: t?.labelsDATA,
              labelsRISK: t?.labelsRISK,
              timeRangeList: t ? t?.timeRangeList || [] : [],
            },
          } as any)
        },
      },
      noppCategory: {
        style: { pointerEvents: disabled ? 'none' : undefined },
        label: 'Nature of Presenting Problem',
        list: [],
        listLabel: (v) => v,
        onChange: (v) => {
          if (v) {
            ref.noppCategory = v
            update()
            api.getState().calculateNopp()
          } else {
            ref.noppCategory = undefined
            update()
          }
        },
      },
    },
  })

  const { emDriver, noppCategory, noteType } = form.controls

  const setDriver = emDriver.value((s) => s.setValue)
  const setNoppCategory = noppCategory.value((s) => s.setValue)
  const disableNopp = noppCategory.disabled((s) => s.setValue)

  const setDrivers = emDriver.list((s) => s.setValue)

  const noppList = noppCategory.list((s) => s.setValue)
  const setNoppList = (value: any[]) => {
    noppList(value || [])
    noppCategory.value.setState({ value: null })
    disableNopp(!value || value.length < 1)
  }

  form.reset = () => {
    const drivers = supportedNotesAndDrivers

    if (drivers?.length) {
      const value = drivers[0]
      noteType.value.setState({ value })

      setNoteType(value)
      update()
    }
  }
  return form
}

function ServiceCategories({ form, nopp, api, icdCpt }) {
  const { noteType, emDriver, noppCategory } = form.controls
  const style = { paddingTop: 24, height: '100%' }

  return (
    <Grid item xs={3} container direction="column" style={style} spacing={1}>
      <Grid item>
        <Auto {...noteType} />
      </Grid>
      <Grid item>
        <Auto {...emDriver} />
      </Grid>
      {nopp && (
        <Grid item>
          <Auto {...noppCategory} />
        </Grid>
      )}
      <Grid item style={{ flexGrow: 1 }}></Grid>
      <Results api={api} icdCpt={icdCpt} />
    </Grid>
  )
}

function CriticalCareFormV2(props: EmCalculatorProps) {
  const { api } = props
  const disabled = api((s) => s.readonly)
  const form = useRef(
    formApi({
      disabled,
      values: {
        totalCriticalCareTime: 0,
        isMedicare: false,
      },
      fields: {
        totalCriticalCareTime: {
          label: '',
          changeOnEnter: true,
          min: 0,
          max: 60 * 500,
          style: { width: '90px' },
          onChange: (v) => {
            const totalCriticalCareTime = parseInt(v) || 0
            const patch: any = { totalCriticalCareTime }
            updateCritCare(patch)
          },
        },
        isMedicare: {
          label: 'Is Medicare?',
          changeOnEnter: true,
          onChange: (v) => {
            const patch: any = { isMedicare: v }
            updateCritCare(patch)
          },
        },
      },
    }),
  ).current

  api.getState().criticalCareForm = form

  useEffect(() => {
    if (disabled) {
      form.disableControls(disabled)
    }
  }, [disabled])

  const { totalCriticalCareTime, isMedicare } = form.controls

  const updateCritCare = (patch: any) => {
    const criticalCare = { ...form.values.getState().value, ...patch }
    const request = api.getState().request
    request.totalCriticalCareTime = criticalCare.totalCriticalCareTime
    request.isMedicare = criticalCare.isMedicare
    api.getState().calculateElements(criticalCare)
  }

  const nowrap: any = { flexWrap: 'nowrap', marginTop: -12 }

  return (
    <Grid
      container
      direction="column"
      spacing={0}
      style={{
        paddingTop: 12,
        paddingLeft: 12,
      }}
    >
      <Grid item>
        <InputLabel style={{ whiteSpace: 'normal', color: 'white' }}>Total Critical Care Time (min.)</InputLabel>
      </Grid>
      <Grid item style={{ flexWrap: 'nowrap' }}>
        <NumberText {...totalCriticalCareTime} />
      </Grid>
      <Grid
        item
        style={{
          paddingTop: 12,
        }}
      ></Grid>

      <Grid item style={nowrap}>
        <CheckBox {...isMedicare} />
      </Grid>
    </Grid>
  )
}
function FieldValue({ label, value }: FieldValueProps) {
  return (
    <Grid item container direction="row" spacing={1} style={{ marginTop: -3 }} alignContent="flex-end">
      <Grid item>
        <Typography style={{ whiteSpace: 'nowrap' }} color="textSecondary">
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <Typography color="textPrimary">{value || ''}</Typography>
      </Grid>
    </Grid>
  )
}
function Results({ icdCpt, api }) {
  const emResult = api((s) => s.result)
  const result: Partial<EmResult> = emResult || ({} as any)

  const onSelectResult = (key: string) => {
    return () => {
      if (icdCpt) {
        icdCpt.setCode(result[key])
      }
    }
  }

  return (
    <>
      <Grid
        item
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        style={{ marginLeft: -3 }}
        spacing={1}
      >
        {result.noppEM > 0 && (
          <Grid item>
            <FieldValue spacing={0} row={true} label="NOPP EM" value={result.noppEM} />
          </Grid>
        )}
        {result.noppEM > 0 && <AddButton tooltip="Add CPT" onClick={onSelectResult('noppEM')} />}
        {result.timeBasedEM > 0 && (
          <Grid item>
            <FieldValue spacing={0} row={true} label="Time Based EM" value={result.timeBasedEM} />
          </Grid>
        )}
        {result.timeBasedEM > 0 && <AddButton tooltip="Add CPT" onClick={onSelectResult('timeBasedEM')} />}
        {result.calculatedEM > 0 && (
          <Grid item>
            <FieldValue spacing={0} row={true} label="Calculated EM" value={result.calculatedEM} />
          </Grid>
        )}
        {result.calculatedEM > 0 && <AddButton tooltip="Add CPT" onClick={onSelectResult('calculatedEM')} />}
      </Grid>
    </>
  )
}

function Errors({ api }) {
  const emResult = api((s) => s.result)
  const result: Partial<EmResult> = emResult || ({} as any)
  const errors = result.successful ? '' : (result.errorMessages || []).join(' ')

  const style: any = { whiteSpace: 'nowrap' }
  const error = {
    ...style,
    ...{
      color: 'white',
      paddingTop: 6,
      fontSize: 11,
      whiteSpace: 'break-spaces',
      width: '100%',
    },
  }

  return (
    <>
      {!result.successful && (
        <Grid item style={{ width: '100%', marginTop: -8 }} container justify="center">
          <div style={error}>{errors.length ? errors : ''}</div>
        </Grid>
      )}
    </>
  )
}

const options: Option[] = [
  {
    label: 'Element',
    value: 'element',
    selected: true,
  },
  {
    label: 'Time ',
    value: 'time',
    hidden: true,
  },
]

function useInterventions(worksheetType: string) {
  return worksheetType === 'OUTPATIENT'
}

function createInterventions(worksheet: Worksheet, api: EmCalculatorStore) {
  const { locationCode, departmentCode, worksheetType } = worksheet
  if (!useInterventions(worksheetType)) {
    return null
  }
  const { config } = api.getState()
  if (!config?.severities?.length || !config?.interventions?.length) {
    return null
  }

  return emInterventionsApi({ locationCode, departmentCode: departmentCode, calculator: api })
}

export function EmCalculator2(props: EmCalculatorProps) {
  const { icdCpt, api } = props

  const conf = api((s) => s.config2)
  const [timeList, setTimeList] = useState([])

  const categories = api.getState().categories
  const interventions = useRef(createInterventions(icdCpt.worksheet, api)).current
  const inputs = interventions ? <EmInterventions api={interventions} /> : <EmCategoryColumns {...categories} />

  const disabled = api((s) => s.readonly)

  //TODO: new time based api
  const disableTime = true
  const typeForm = useRef(
    formApi({
      disabled,
      values: { calcType: 'element', timeRange: '' },
      fields: {
        calcType: {
          disabled: disableTime,
          options,
          row: true,
          style: {},
        },
        timeRange: {
          options: timeList.map((t) => ({
            label: t,
            value: t,
            disabled: disableTime,
          })),
          onChange: (timeRange) => calc({ ...api.getState().request, ...{ timeRange } }),
          style: { height: '48px', display: 'block' },
        },
      },
    }),
  ).current

  const { calcType: calcT, timeRange } = typeForm.controls

  const setTimeRangeOptions = timeRange.options((s) => s.setValue)
  const setCalcTypeOptions = calcT.options((s) => s.setValue)

  const calcType = calcT.value((s) => s.value)
  const setCalType = calcT.value((s) => s.setValue)

  const calc = (request: any) => {
    if (calcT.value.getState().value !== 'time') {
      api.getState().calculateElements(request)
    } else {
      api.getState().calculateTime(request)
    }
  }

  const onDriverChange = (v: Driver) => {
    categories.setDriver(v)
    options[1].disabled = disableTime

    categories.clear()
    api.setState({
      request: { opt: undefined, data: undefined, risk: undefined },
    })

    // const disableTime = true; // !v.timeRangeList.length;
    //TODO: ask if type should always be set on dri`ver change
    if (disableTime) {
      setCalType('element')
    }

    setTimeList(v.timeRangeList)
    setTimeRangeOptions(
      v.timeRangeList.map((t: string) => ({
        label: t,
        value: t,
        disabled: disableTime,
      })),
    )
    options[1].disabled = disableTime
    setCalcTypeOptions(options.slice())
  }

  const timeRanges = <RadioGroup {...timeRange} />

  const form = useRef(
    serviceCategories({
      api,
      conf,
      onCategoryChange: () => {
        //  console.log('onCategoryChange element ')
        typeForm.setValues({ calcType: 'element' })
        setCalType('element')
        options[1].disabled = true
      },
      onDriverCleared: () => {
        categories.setDriver({
          labelsOPT: [],
          labelsDATA: [],
          labelsRISK: [],
        } as any)
      },
      onDriverChange: (v) => onDriverChange(v),
    }),
  ).current

  api.getState().serviceCategoryForm = form

  useEffect(() => {
    const n = form.controls.noteType.value.getState().value
    const d = form.controls.emDriver.value.getState().value
    if (n && d) {
      categories.setDriver(d)
      const req = api.getState().request
      api.setState({
        request: {
          ...req,
          ...{ noteType: n.noteType, emDriver: d.driverName },
        },
      })
    }
    if (api.getState().reset) {
      form.reset()
      api.setState({ reset: false })
    }
  }, [])

  useEffect(() => {
    form.disableControls(disabled)

    return api.subscribe(
      (request) => {
        calc(request)
      },
      (state) => state.request,
    )
  }, [disabled])

  const controlValues = timeRange.options((s) => s.value)
  const opt = calcT.options((s) => s.value)
  opt[1].disabled = disableTime || controlValues.length === 0

  return (
    <div
      style={{
        width: '100%',
        minHeight: 156,
        color: 'white',
        paddingLeft: 12,
        paddingRight: 12,
        marginTop: -12,
        paddingBottom: 12,
        borderBottom: `solid 1px ${lightGray}`,
      }}
    >
      <Grid
        item
        container
        direction="row"
        wrap="nowrap"
        style={{ width: '100%', overflow: 'hidden' }}
        alignItems="flex-start"
        justify="center"
        spacing={1}
      >
        <ServiceCategories form={form} nopp={false} api={api} icdCpt={icdCpt} />
        {api.getState().required && (
          <Grid
            style={{
              color: 'white',
              position: 'relative',
              whiteSpace: 'nowrap',
              fontStyle: 'italic',
              marginTop: 12,
            }}
          >
            * Required For EM Codes
          </Grid>
        )}
        <Grid
          item
          xs={7}
          container
          direction="column"
          spacing={1}
          wrap={'nowrap'}
          style={{ paddingTop: 12, flexGrow: 1, overflow: 'hidden' }}
          alignItems="center"
          justify="center"
        >
          <RadioGroup {...typeForm.controls.calcType} />

          {disableTime || calcType !== 'time' ? inputs : timeRanges}

          <Errors api={api} />
        </Grid>

        <Grid xs={2} item style={{ paddingTop: 16, paddingLeft: 24 }}>
          <CriticalCareFormV2 {...props} />
        </Grid>
      </Grid>
    </div>
  )
}
